import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { getFirebaseAuth, googleProvider, isAllowedEmailDomain, getAllowedEmailDomains } from './firebase'
import { generateGuestUser } from './guestNames'
import { getDb } from '../slides/services/firebase'
import { syncUserToFirestore, subscribeUserRole } from '../slides/services/userService'

export interface AppUser {
  uid: string
  email: string
  displayName: string
  photoURL?: string | null
  initials: string
  color: string
  isGuest?: boolean
  role?: 'admin' | 'editor' | 'viewer'
}

const AVATAR_COLORS = [
  '#0078D4', // Blue
  '#107C41', // Emerald
  '#D83B01', // Coral/Orange
  '#8764B8', // Purple
  '#008272', // Teal
  '#E3008C', // Magenta
  '#004E8C', // Navy
]

export const getInitials = (name: string): string => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export const getAuthorColor = (name: string): string => {
  if (!name) return AVATAR_COLORS[0]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length
  return AVATAR_COLORS[index]
}

export const formatAuthErrorMessage = (err: any): { message: string; code?: string } => {
  if (!err) return { message: 'Wystąpił nieoczekiwany błąd uwierzytelniania.' }
  const code = err?.code || ''
  if (code === 'auth/configuration-not-found') {
    return {
      code,
      message:
        'Dostawca logowania Google nie został jeszcze włączony w projekcie Firebase (mamf-slides). Włącz dostawcę "Google" w Firebase Console (Authentication -> Sign-in method).',
    }
  }
  if (code === 'auth/operation-not-allowed') {
    return {
      code,
      message:
        'Logowanie przez Google jest wyłączone w Firebase Console (Authentication -> Sign-in method).',
    }
  }
  if (code === 'auth/unauthorized-domain') {
    return {
      code,
      message:
        'Bieżąca domena (localhost) nie jest dodana do autoryzowanych domen w Firebase Console (Authentication -> Settings -> Authorized domains).',
    }
  }
  if (code === 'auth/popup-blocked') {
    return {
      code,
      message: 'Przeglądarka zablokowała wyskakujące okno logowania Google. Zezwól na pop-upy i spróbuj ponownie.',
    }
  }
  return {
    code,
    message: err?.message || 'Wystąpił błąd podczas logowania przez Google.',
  }
}

const DEV_USER_STORAGE_KEY = 'mamf_dev_auth_user'
const GUEST_USER_STORAGE_KEY = 'mamf_guest_user'
export const AUTH_GATE_STORAGE_KEY = 'mamf_auth_gate_enabled'

export const DEFAULT_DEV_USER: AppUser = {
  uid: 'dev_jakub_nagiet',
  email: 'jakub.nagiet@mamf.pl',
  displayName: 'Jakub Nagiet',
  photoURL: null,
  initials: 'JN',
  color: '#0078D4',
}

export const isAuthGateConfigured = (): boolean => {
  if (typeof window === 'undefined') return true
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('auth') === '0' || params.get('noauth') === '1') {
      localStorage.setItem(AUTH_GATE_STORAGE_KEY, 'false')
      return false
    }
    if (params.get('auth') === '1') {
      localStorage.setItem(AUTH_GATE_STORAGE_KEY, 'true')
      return true
    }
  } catch {}

  try {
    const stored = localStorage.getItem(AUTH_GATE_STORAGE_KEY)
    if (stored !== null) {
      return stored === 'true'
    }
  } catch {}

  if (process.env.GATSBY_AUTH_ENABLED === 'false' || process.env.GATSBY_DISABLE_AUTH === 'true') {
    return false
  }
  return true
}

export const ADMIN_EMAILS = [
  'jakub.nagiet@mamf.pl',
  'jakub.nagiet@meandmyfriends.com',
]

export const isAdminUser = (u: AppUser | null | undefined): boolean => {
  if (!u || u.isGuest) return false
  const email = (u.email || '').toLowerCase().trim()
  if (!email) return false
  if (ADMIN_EMAILS.includes(email)) return true
  const envAdmins = process.env.GATSBY_ADMIN_EMAILS
  if (envAdmins) {
    const list = envAdmins.split(',').map((e) => e.toLowerCase().trim()).filter(Boolean)
    if (list.includes(email)) return true
  }
  if (u.role === 'admin') return true
  if (u.uid?.startsWith('dev_') && email.includes('jakub.nagiet')) return true
  return false
}

export const isEditorUser = (u: AppUser | null | undefined): boolean => {
  if (!u) return false
  if (isAdminUser(u)) return true
  if (u.role === 'editor' || u.role === 'admin') return true
  if (u.isGuest && u.displayName?.toLowerCase().trim() === 'team') return true
  return false
}

export interface AuthContextType {
  user: AppUser | null
  isAdmin: boolean
  isEditor: boolean
  loading: boolean
  error: string | null
  errorCode: string | null
  isAuthGateEnabled: boolean
  toggleAuthGate: (enabled?: boolean) => void
  loginWithGoogle: () => Promise<void>
  loginWithDevAccount: (email: string, name?: string) => boolean
  loginAsGuest: (nickname?: string, role?: 'editor' | 'viewer') => void
  logout: () => Promise<void>
  deleteAccount: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const existing = useContext(AuthContext)
  if (existing) {
    return <>{children}</>
  }

  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [isAuthGateEnabled, setIsAuthGateEnabled] = useState<boolean>(() => isAuthGateConfigured())

  const toggleAuthGate = useCallback((enabled?: boolean) => {
    setIsAuthGateEnabled((prev) => {
      const next = typeof enabled === 'boolean' ? enabled : !prev
      try {
        localStorage.setItem(AUTH_GATE_STORAGE_KEY, String(next))
      } catch {}
      return next
    })
  }, [])

  const clearError = useCallback(() => {
    setError(null)
    setErrorCode(null)
  }, [])

  // Listen to Firebase auth state with browserLocalPersistence
  useEffect(() => {
    const auth = getFirebaseAuth()
    if (!auth) {
      setLoading(false)
      return
    }

    let userRoleUnsub: (() => void) | null = null

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (userRoleUnsub) {
        userRoleUnsub()
        userRoleUnsub = null
      }

      if (firebaseUser) {
        // Validate domain protection
        if (!isAllowedEmailDomain(firebaseUser.email)) {
          const allowed = getAllowedEmailDomains().map((d) => `@${d}`).join(' lub ')
          setError(`Brak uprawnień. Dostęp jest zastrzeżony wyłącznie dla kont w domenie ${allowed}.`)
          setErrorCode('domain-restricted')
          try {
            await signOut(auth)
          } catch (signOutErr) {
            console.warn('Error signing out unauthorized user:', signOutErr)
          }
          setUser(null)
          setLoading(false)
          return
        }

        const displayName =
          firebaseUser.displayName?.trim() ||
          firebaseUser.email?.split('@')[0] ||
          'Pracownik'

        let effectiveRole: 'admin' | 'editor' | 'viewer' = 'viewer'
        try {
          const synced = await syncUserToFirestore({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName,
            photoURL: firebaseUser.photoURL,
          })
          effectiveRole = synced.role
        } catch (syncErr) {
          console.warn('Could not sync user with Firestore:', syncErr)
        }

        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName,
          photoURL: firebaseUser.photoURL,
          initials: getInitials(displayName),
          color: getAuthorColor(displayName),
          role: effectiveRole,
        })
        setError(null)
        setErrorCode(null)

        // Subscribe to realtime role changes for the current user
        try {
          userRoleUnsub = subscribeUserRole(
            firebaseUser.uid,
            firebaseUser.email || '',
            (newRole) => {
              setUser((prev) => (prev ? { ...prev, role: newRole } : null))
            }
          )
        } catch (listenErr) {
          console.warn('Could not subscribe to user role updates:', listenErr)
        }

        try {
          localStorage.removeItem(DEV_USER_STORAGE_KEY)
          localStorage.removeItem(GUEST_USER_STORAGE_KEY)
        } catch {}
      } else {
        // Check for local guest session if Firebase Auth has no active user
        try {
          const cachedGuest = typeof window !== 'undefined' ? localStorage.getItem(GUEST_USER_STORAGE_KEY) : null
          if (cachedGuest) {
            const parsed = JSON.parse(cachedGuest) as AppUser
            if (parsed && parsed.displayName) {
              if (parsed.isGuest && !parsed.initials) {
                const parts = parsed.displayName.trim().split(/\s+/)
                parsed.initials = (parts.length >= 2 ? parts[parts.length - 1].charAt(0) : parsed.displayName.charAt(0) || 'G').toUpperCase()
              }
              if (parsed.displayName?.toLowerCase().trim() === 'team') {
                parsed.role = 'editor'
                parsed.initials = 'TM'
                parsed.color = '#F04248'
              }
              setUser(parsed)
              setLoading(false)
              return
            }
          }
        } catch {}
        // Check for local dev session if Firebase Auth has no active user
        try {
          const cached = typeof window !== 'undefined' ? localStorage.getItem(DEV_USER_STORAGE_KEY) : null
          if (cached) {
            const parsed = JSON.parse(cached) as AppUser
            if (isAllowedEmailDomain(parsed.email)) {
              setUser(parsed)
              setLoading(false)
              return
            }
          }
        } catch {}
        setUser(null)
      }
      setLoading(false)
    })

    return () => {
      unsubscribe()
      if (userRoleUnsub) {
        userRoleUnsub()
      }
    }
  }, [])

  const loginWithGoogle = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (!auth) {
      setError('Firebase Auth nie jest dostępny w tym środowisku.')
      return
    }

    setError(null)
    setErrorCode(null)
    setLoading(true)

    try {
      const result = await signInWithPopup(auth, googleProvider)
      const signedUser = result.user

      if (!isAllowedEmailDomain(signedUser.email)) {
        const allowed = getAllowedEmailDomains().map((d) => `@${d}`).join(' lub ')
        await signOut(auth)
        setUser(null)
        setError(`Adres ${signedUser.email} nie ma uprawnień dostępu. Użyj konta w domenie ${allowed}.`)
        setErrorCode('domain-restricted')
        setLoading(false)
        return
      }

      const displayName =
        signedUser.displayName?.trim() ||
        signedUser.email?.split('@')[0] ||
        'Pracownik'

      let effectiveRole: 'admin' | 'editor' | 'viewer' = 'viewer'
      try {
        const synced = await syncUserToFirestore({
          uid: signedUser.uid,
          email: signedUser.email || '',
          displayName,
          photoURL: signedUser.photoURL,
        })
        effectiveRole = synced.role
      } catch {}

      setUser({
        uid: signedUser.uid,
        email: signedUser.email || '',
        displayName,
        photoURL: signedUser.photoURL,
        initials: getInitials(displayName),
        color: getAuthorColor(displayName),
        role: effectiveRole,
      })
      try {
        localStorage.removeItem(DEV_USER_STORAGE_KEY)
      } catch {}
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user') {
        // User voluntarily closed popup
        setError(null)
        setErrorCode(null)
      } else if (err?.code === 'auth/cancelled-popup-request') {
        // Duplicate popup ignored
      } else {
        console.error('Firebase Auth error during Google sign-in:', err)
        const formatted = formatAuthErrorMessage(err)
        setError(formatted.message)
        setErrorCode(formatted.code || null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const loginWithDevAccount = useCallback((email: string, name?: string): boolean => {
    const cleanEmail = email.trim().toLowerCase()
    if (!isAllowedEmailDomain(cleanEmail)) {
      const allowed = getAllowedEmailDomains().map((d) => `@${d}`).join(' lub ')
      setError(`Adres ${cleanEmail} nie ma uprawnień dostępu. Wymagana domena ${allowed}.`)
      setErrorCode('domain-restricted')
      return false
    }

    const cleanName =
      name?.trim() ||
      cleanEmail
        .split('@')[0]
        .split('.')
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ')

    const devUser: AppUser = {
      uid: `dev_${cleanEmail.replace(/[^a-zA-Z0-9]/g, '_')}`,
      email: cleanEmail,
      displayName: cleanName,
      photoURL: null,
      initials: getInitials(cleanName),
      color: getAuthorColor(cleanName),
    }

    try {
      localStorage.setItem(DEV_USER_STORAGE_KEY, JSON.stringify(devUser))
      localStorage.removeItem(GUEST_USER_STORAGE_KEY)
    } catch {}

    setUser(devUser)
    setError(null)
    setErrorCode(null)
    return true
  }, [])

  const loginAsGuest = useCallback((nickname?: string, role?: 'editor' | 'viewer') => {
    let cleanNick = (nickname || '').trim()
    let initials = ''
    let color = ''

    const isTeam = cleanNick.toLowerCase() === 'team' || role === 'editor'
    const effectiveRole: 'editor' | 'viewer' = isTeam ? 'editor' : 'viewer'

    if (isTeam) {
      cleanNick = cleanNick || 'Team'
      initials = 'TM'
      color = '#F04248'
    } else if (!cleanNick) {
      const generated = generateGuestUser()
      cleanNick = generated.name
      initials = generated.initials
      color = generated.color
    } else {
      const parts = cleanNick.split(/\s+/)
      // If user typed 2 words (e.g. Adjective Animal), use animal's initial, otherwise first letter
      initials = (parts.length >= 2 ? parts[parts.length - 1].charAt(0) : cleanNick.charAt(0)).toUpperCase()
      color = getAuthorColor(cleanNick)
    }

    const guestUid = `guest_${cleanNick.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'user'}_${Date.now().toString(36)}`
    const guestUser: AppUser = {
      uid: guestUid,
      email: `${cleanNick.toLowerCase().replace(/[^a-z0-9]/g, '.') || 'gosc'}@gosc.local`,
      displayName: cleanNick,
      photoURL: null,
      initials,
      color,
      isGuest: true,
      role: effectiveRole,
    }

    try {
      localStorage.setItem(GUEST_USER_STORAGE_KEY, JSON.stringify(guestUser))
      localStorage.removeItem(DEV_USER_STORAGE_KEY)
    } catch {}

    setUser(guestUser)
    setError(null)
    setErrorCode(null)
  }, [])

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem(DEV_USER_STORAGE_KEY)
      localStorage.removeItem(GUEST_USER_STORAGE_KEY)
    } catch {}

    const auth = getFirebaseAuth()
    if (auth) {
      try {
        await signOut(auth)
      } catch (err: any) {
        console.error('Firebase Auth sign-out error:', err)
      }
    }

    setUser(null)
    setError(null)
    setErrorCode(null)
  }, [])

  const deleteAccount = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (auth?.currentUser) {
      try {
        await auth.currentUser.delete()
      } catch (err: any) {
        console.warn('Firebase Auth delete account error:', err)
        try {
          await signOut(auth)
        } catch {}
      }
    }

    try {
      localStorage.removeItem(DEV_USER_STORAGE_KEY)
      localStorage.removeItem(GUEST_USER_STORAGE_KEY)
      const toRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && (k.startsWith('mamf_user_') || k.startsWith('mamf_guest_'))) {
          toRemove.push(k)
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k))
    } catch {}

    setUser(null)
    setError(null)
    setErrorCode(null)
  }, [])

  // If Auth Gate is disabled for testing, provide default verified team identity if not logged in
  const effectiveUser = user || (!isAuthGateEnabled ? DEFAULT_DEV_USER : null)
  const isAdmin = Boolean(effectiveUser && isAdminUser(effectiveUser))
  const isEditor = Boolean(effectiveUser && isEditorUser(effectiveUser))

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        isAdmin,
        isEditor,
        loading,
        error,
        errorCode,
        isAuthGateEnabled,
        toggleAuthGate,
        loginWithGoogle,
        loginWithDevAccount,
        loginAsGuest,
        logout,
        deleteAccount,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    return {
      user: null,
      isAdmin: false,
      isEditor: false,
      loading: false,
      error: null,
      errorCode: null,
      isAuthGateEnabled: true,
      toggleAuthGate: () => {},
      loginWithGoogle: async () => {},
      loginWithDevAccount: () => false,
      loginAsGuest: () => {},
      logout: async () => {},
      deleteAccount: async () => {},
      clearError: () => {},
    }
  }
  return context
}
